// Api.js
import { db } from './firebase'; // <-- Your firebase.js
import {
  ref,
  get,
  query,
  orderByChild,
  startAt,
  endAt,
  limitToLast,
  set,
} from 'firebase/database';
import { getAuth } from 'firebase/auth';

class Api {
   /**
   * Retorna la suma total de facturas y retornos (en USD).
   * Si se especifica `fecha`, solo se sumarán los items cuya `fecha <= fecha`.
   *
   * @param {string} user - nombre/ID del usuario
   * @param {number|null} fecha - Epoch timestamp (opcional). Si null, trae todo.
   * @returns {Promise<{ facturas: number; retornos: number }>}
   */
   async saldoTotal(user, fecha = null) {
    let facturasRef = ref(db, `/${user}/facturas`);
    let retornosRef = ref(db, `/${user}/retornos`);

    // Si se pasa `fecha`, creamos queries que terminan en `fecha`
    // (i.e., items con fecha <= `fecha`)
    if (fecha !== null) {
      facturasRef = query(facturasRef, orderByChild("fecha"), endAt(fecha));
      retornosRef = query(retornosRef, orderByChild("fecha"), endAt(fecha));
    }

    // Cargamos ambos snaps en paralelo
    const [facturasSnap, retornosSnap] = await Promise.all([
      get(facturasRef),
      get(retornosRef),
    ]);

    let facturasTotal = 0;
    if (facturasSnap.exists()) {
      facturasSnap.forEach((child) => {
        const val = child.val().monto_usd || 0;
        facturasTotal += val;
      });
    }

    let retornosTotal = 0;
    if (retornosSnap.exists()) {
      retornosSnap.forEach((child) => {
        const val = child.val().monto_usd || 0;
        retornosTotal += val;
      });
    }

    return {
      facturas: Math.round(facturasTotal),
      retornos: Math.round(retornosTotal),
    };
  }

  /**
   * Retrieves the most recent "facturas" (boletas) for the user, 
   * sorted by `fecha` descending, limited to `limit`.
   * 
   * @param {string} user
   * @param {number} limit
   * @returns {Promise<Object>} An object keyed by push IDs (or empty object).
   */
  async boletasLast(user, limit = 10) {
    const q = query(
      ref(db, `/${user}/facturas`),
      orderByChild('fecha'),
      limitToLast(limit)
    );
    const snapshot = await get(q);
    return snapshot.exists() ? snapshot.val() : {};
  }

  /**
   * Retrieves the most recent "retornos" for the user, 
   * sorted by `fecha` descending, limited to `limit`.
   * 
   * @param {string} user
   * @param {number} limit
   * @returns {Promise<Object>}
   */
  async retornosLast(user, limit = 10) {
    const q = query(
      ref(db, `/${user}/retornos`),
      orderByChild('fecha'),
      limitToLast(limit)
    );
    const snapshot = await get(q);
    return snapshot.exists() ? snapshot.val() : {};
  }

  /**
   * Retrieves the most recent "cobros" for the user,
   * sorted by `fecha` descending, limited to `limit`.
   * 
   * @param {string} user
   * @param {number} limit
   * @returns {Promise<Object>}
   */
  async cobrosLast(user, limit = 10) {
    const q = query(
      ref(db, `/${user}/cobros`),
      orderByChild('fecha'),
      limitToLast(limit)
    );
    const snapshot = await get(q);
    return snapshot.exists() ? snapshot.val() : {};
  }

  /**
   * Retrieves "facturas" (boletas) by a given date range [fromEpoch, toEpoch].
   * Returns an array of objects sorted by `fecha` descending.
   * 
   * @param {string} user
   * @param {number} fromEpoch
   * @param {number} toEpoch
   * @returns {Promise<Array<Object>>}
   */
  async boletasFecha(user, fromEpoch, toEpoch) {
    const q = query(
      ref(db, `/${user}/facturas`),
      orderByChild('fecha'),
      startAt(fromEpoch),
      endAt(toEpoch)
    );

    const snapshot = await get(q);
    if (!snapshot.exists()) {
      return [];
    }

    // Convert to array and sort descending
    const data = snapshot.val();
    return Object.keys(data)
      .map((key) => ({
        id: key,
        ...data[key],
      }))
      .sort((a, b) => b.fecha - a.fecha);
  }

  /**
   * Retrieves "retornos" by a given date range [fromEpoch, toEpoch].
   * Returns an array of objects sorted by `fecha` descending.
   * 
   * @param {string} user
   * @param {number} fromEpoch
   * @param {number} toEpoch
   * @returns {Promise<Array<Object>>}
   */
  async retornosFecha(user, fromEpoch, toEpoch) {
    const q = query(
      ref(db, `/${user}/retornos`),
      orderByChild('fecha'),
      startAt(fromEpoch),
      endAt(toEpoch)
    );
    const snapshot = await get(q);
    if (!snapshot.exists()) {
      return [];
    }
    const data = snapshot.val();
    return Object.keys(data)
      .map((key) => ({
        id: key,
        ...data[key],
      }))
      .sort((a, b) => b.fecha - a.fecha);
  }

  /**
   * Retrieves "cobros" by a given date range [fromEpoch, toEpoch].
   * Returns an array of objects sorted by `fecha` descending.
   * 
   * @param {string} user
   * @param {number} fromEpoch
   * @param {number} toEpoch
   * @returns {Promise<Array<Object>>}
   */
  async cobrosFecha(user, fromEpoch, toEpoch) {
    const q = query(
      ref(db, `/${user}/cobros`),
      orderByChild('fecha'),
      startAt(fromEpoch),
      endAt(toEpoch)
    );
    const snapshot = await get(q);
    if (!snapshot.exists()) {
      return [];
    }
    const data = snapshot.val();
    return Object.keys(data)
      .map((key) => ({
        id: key,
        ...data[key],
      }))
      .sort((a, b) => b.fecha - a.fecha);
  }

  /**
   * Updates a "boleta" (factura) in the database.
    *
    * @param {Object} boleta
    * @returns {Promise<void>}
    */
  async updateBoleta(boleta) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No hay un usuario autenticado para actualizar.');
    }
    await set(ref(db, `/${user.displayName}/facturas/${boleta.id}`), boleta);
  }

  async getImage(imageId) {
    try {
      const telegramToken = "7881554252:AAHrS7B39FdaU9TNolYwRLvLCjNn5Mn7M70";
      
      // Obtener la ruta del archivo en Telegram
      const filePathUrl = `https://api.telegram.org/bot${telegramToken}/getFile?file_id=${imageId}`;
      const filePathResponse = await fetch(filePathUrl);
      const filePathJson = await filePathResponse.json();

      if (!filePathJson.ok) throw new Error("No se pudo obtener la ruta del archivo");

      const filePath = filePathJson.result.file_path;
      
      // Obtener el archivo en formato binario
      const fileUrl = `https://api.telegram.org/file/bot${telegramToken}/${filePath}`;
      return fileUrl;
    } catch (error) {
      console.error("Error al obtener la imagen desde Telegram:", error);
      return null;
    }
  }

  
  colorsMap = {
      'REPUESTO': 'darkblue',
      'COMBUSTIBLE': 'brown',
      'COMESTIBLE': 'purple',
      'SUELDO': 'orange',
      'SERVICIO': 'gray',
      'INSUMO_AGRICOLA': 'green',
      'INSUMO_GANADERO': 'darkgray',
      'IMPUESTO': 'red',
      'RETIROS': 'dodgerblue',
      'OTRO': 'white',
  }
  
}

// You can export the class or an instance
const apiInstance = new Api();
export default apiInstance;
